import styles from "./StockInfo.module.scss";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { Stock } from "@/models/stock";
import { classNames, getCurrentSessionInterval } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { textColorState, themeState } from "@/lib/store";
import IndicatorBar from "../indicator-bar/IndicatorBar";
import { Context } from "@/lib/Context";
import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";
import Skeleton from "../skeleton/Skeleton";
import StockInfoDetails from "./StockInfoDetails";
import StockChainSelector from "./StockChainSelector";
import StockCommentary from "../stock-commentary/StockCommentary";

const GB_CURRENCIES = ["GBP", "GBX"];

const StockInfo: FC<{ stock: Stock; isCustomView?: boolean }> = ({ stock, isCustomView = true }) => {
    const { ortexWebSocket } = useContext(Context);
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);

    const [isLoading, setIsLoading] = useState(true);
    const [yearRanges, setYearRanges] = useState<{ high: number; low: number }>({ high: stock?.high_52_week, low: stock?.low_52_week });
    const [dayRanges, setDayRanges] = useState<{ high: number; low: number }>({ high: undefined, low: undefined });
    const [price, setPrice] = useState<number>();

    const high52Week = price > stock?.high_52_week ? price : stock?.high_52_week < dayRanges.high ? dayRanges.high : stock?.high_52_week;
    const low52Week = price < stock?.low_52_week ? price : stock?.low_52_week > dayRanges.low ? dayRanges.low : stock?.low_52_week;
    const hide52WeekBar = low52Week > high52Week || (low52Week === null && high52Week === null);

    const isLiveFeed = stock?.livePriceFeed;
    const websocket = isLiveFeed === "NA" || isLiveFeed === "EU" ? ortexWebSocket : null;

    const identifier = isLiveFeed === "NA" ? stock?.ticker : isLiveFeed === "EU" ? `${stock?.isin}${stock?.currency}` : null;
    const GBPIdentifier = isLiveFeed === "EU" ? `${stock?.isin}GBP` : null;
    const GBXIdentifier = isLiveFeed === "EU" ? `${stock?.isin}GBX` : null;

    const [isGBCurrency] = useState(GB_CURRENCIES.includes(stock?.currency));

    const message = { type: "last_trades_history_w_ticker", identifier };

    const indicatorBarLabelStyle = { color: textColor, ...(isCustomView && { fontSize: "0.6rem" }) };

    const sendMessage = useCallback(() => {
        if (isLiveFeed === "EU" && GB_CURRENCIES.includes(stock?.currency)) {
            websocket.send(JSON.stringify({ type: "last_trades_history_w_ticker", identifier: GBPIdentifier }));
            websocket.send(JSON.stringify({ type: "last_trades_history_w_ticker", identifier: GBXIdentifier }));
        } else if (isLiveFeed === "NA") {
            websocket.send(JSON.stringify(message));
        }
    }, [stock?.stock_id, websocket]);

    const updateData = useCallback(
        ({ data }: MessageEvent) => {
            const parsedData = JSON.parse(data);

            const ISIN: string = parsedData.ISIN;
            const tsLast: [number, number] = parsedData["ts-last"];
            const ts: [number, number, number, number, number][] = parsedData.ts;
            const T: { C: { o: number; h: number; l: number; c: number }; i: string } = parsedData.T;
            const LT: [{ o: number; h: number; l: number; c: number }, number][] = parsedData.LT;

            const isUSTicker = parsedData.ticker === stock.ticker;
            const isEuropeanTicker = parsedData.ticker === stock.isin + stock.currency;
            const isGBPCurrency = parsedData.ticker === GBPIdentifier;
            const isGBXCurrency = parsedData.ticker === GBXIdentifier;

            if (LT && (isUSTicker || isEuropeanTicker || (isGBCurrency && (isGBPCurrency || isGBXCurrency)))) {
                const sessions = stock?.exchange_th.split("-");
                const beforeMarketOpenDiff = getCurrentSessionInterval([sessions[0] + "-" + sessions[sessions.length - 1]], stock?.exchange_tz) !== -1 ? 0 : dayjs().day() === 1 ? 3 : 1;
                const open = dayjs.tz(sessions[0], "HHmm", stock?.exchange_tz).subtract(beforeMarketOpenDiff, "days");
                const close = dayjs.tz(sessions[sessions.length - 1], "HHmm", stock?.exchange_tz).subtract(beforeMarketOpenDiff, "days");
                const hasOldData = dayjs(LT?.[0]?.[1]).isBefore(open);
                const trades = hasOldData ? LT.filter((value) => dayjs(value[1]).isBetween(open, close)) : LT;
                if (trades.length) {
                    const highs = trades.map((value) => value[0].h);
                    const lows = trades.map((value) => value[0].l);
                    setDayRanges({ high: Math.max(...highs), low: Math.min(...lows) });
                    const closePrice = trades[trades.length - 1]?.[0].c;
                    if (!isFinite(price) && isFinite(closePrice)) setPrice(closePrice);
                }
            } else if (ts && ISIN === stock?.isin) {
                const currentDate = dayjs();
                const trades = ts.filter(([time]) => dayjs(time).isSame(currentDate, "day"));
                if (trades.length) {
                    const highs = trades.map((value) => value[2]);
                    const lows = trades.map((value) => value[3]);
                    setDayRanges({ high: Math.max(...highs), low: Math.min(...lows) });
                    const closePrice = trades[trades.length - 1]?.[4];
                    if (!isFinite(price) && isFinite(closePrice)) setPrice(closePrice);
                }
            } else if (ISIN === stock?.isin && isFinite(tsLast?.[1])) {
                const closePrice = tsLast[1];
                setPrice(closePrice);
            } else if (T?.i === stock?.ticker) {
                const closePrice = T.C.c;
                setPrice(closePrice);
            }
            setIsLoading(false);
        },
        [stock?.stock_id, websocket, isGBCurrency]
    );

    useEffect(() => {
        setPrice(undefined);
        setDayRanges({ high: undefined, low: undefined });

        if (isLiveFeed && websocket) {
            websocket.readyState === 1 ? sendMessage() : websocket.addEventListener("open", sendMessage);
            websocket.addEventListener("message", updateData);
        } else {
            setIsLoading(false);
        }

        return () => {
            if (isLiveFeed && websocket) {
                websocket.removeEventListener("open", sendMessage);
                websocket.removeEventListener("message", updateData);
            }
        };
    }, [stock?.stock_id, isLiveFeed, isLiveFeed, websocket]);

    useEffect(() => {
        if (isFinite(price)) {
            if (price < dayRanges.low) setDayRanges((ranges) => ({ ...ranges, low: price }));
            if (price > dayRanges.high) setDayRanges((ranges) => ({ ...ranges, high: price }));
            if (price < yearRanges.low) setYearRanges((ranges) => ({ ...ranges, low: price }));
            if (price > yearRanges.high) setYearRanges((ranges) => ({ ...ranges, high: price }));
        }
    }, [price]);

    if (hide52WeekBar) return null;

    // A live stock keeps the day bar's half from the start, empty until its price arrives: the
    // 52-week bar used to be drawn full width and then halved, a layout shift on every stock page.
    const daySlot = isFinite(price) || isLiveFeed === "NA" || isLiveFeed === "EU";

    // In the overview's header the bars have no height of their own to fill, so a "100%" skeleton
    // was 0px and the header grew when the first trade arrived (a layout shift on every stock page).
    // There the skeleton keeps the bars' size.
    if (isLoading && !isCustomView)
        return (
            <Box className={styles["loading-slot"]}>
                <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
            </Box>
        );

    return isLoading ? (
        <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
    ) : (
        <Box className={classNames(styles[theme], styles.StockInfo)}>
            <Box className={styles["box-indicator-bars"]} sx={{ display: "flex", flexDirection: "column" }}>
                {isCustomView && (
                    <Box sx={{ width: "98%", marginX: "auto" }}>
                        <Typography className={styles["bar-title"]} style={{ color: textColor }}>
                            Price Range
                        </Typography>
                    </Box>
                )}
                <Box sx={{ width: "100%", marginX: "auto", display: "flex", flexDirection: "row", justifyContent: "space-between" }} className={isCustomView ? styles["indicators-container-custom-views"] : styles["indicators-container"]}>
                    {daySlot && !isFinite(price) && <Box sx={{ width: "47%" }} />}
                    {isFinite(price) && (
                        <Box sx={{ width: "47%", position: "relative", top: "-5px" }}>
                            <p style={{ display: "flex", justifyContent: "space-between", width: "100%" }} className={styles["indicator-bar-label-container"]}>
                                <span className={styles["indicator-bar-label"]} style={indicatorBarLabelStyle}>
                                    {dayRanges.low?.toFixed(2) ?? "N/A"}
                                </span>
                                <span className={styles["indicator-bar-label"]} style={indicatorBarLabelStyle}>
                                    {dayRanges.high?.toFixed(2) ?? "N/A"}
                                </span>
                            </p>
                            <Box>
                                <IndicatorBar percentage={((price - dayRanges.low) / (dayRanges.high - dayRanges.low)) * 100} textAlign="center" title="Day" titleWeight="700" titleFont={isCustomView ? "0.6rem" : "0.8rem"} />
                            </Box>
                        </Box>
                    )}
                    <Box sx={{ width: daySlot ? "47%" : "100%", position: "relative", top: "-5px" }}>
                        <p style={{ display: "flex", justifyContent: "space-between", width: "100%" }} className={styles["indicator-bar-label-container"]}>
                            <span className={styles["indicator-bar-label"]} style={indicatorBarLabelStyle}>
                                {low52Week?.toFixed(2) ?? "N/A"}
                            </span>
                            <span className={styles["indicator-bar-label"]} style={indicatorBarLabelStyle}>
                                {high52Week?.toFixed(2) ?? "N/A"}
                            </span>
                        </p>
                        <Box>
                            <IndicatorBar noPointer={!isFinite(price)} percentage={((price - low52Week) / (high52Week - low52Week)) * 100} textAlign="center" title="52 Week" titleWeight="700" titleFont={isCustomView ? "0.6rem" : "0.8rem"} />
                        </Box>
                    </Box>
                </Box>
            </Box>
            {isCustomView && (
                <>
                    <StockCommentary stock={stock} isCustomView />
                    <StockInfoDetails stock={stock} isCustomView />
                    <StockChainSelector stock={stock} isCustomView />
                </>
            )}
        </Box>
    );
};

export default StockInfo;
